/**
 * OptimizationCardV2. Design preview, not shipped to production.
 *
 * Mirrors the embryo-freezing "Optimize Before Your Cycle" stepper
 * step (image-v18 from the design review). Step header, two paragraphs
 * of real context, per-category rows with substantial specific
 * subtitles (dosages, behavioral guidance, partner content), Guided
 * Program row at the foot.
 *
 * Differences from the embryo card:
 *   1. Each subcategory is a canonical row (glass-card chrome + image
 *      left of full title-and-subtitle column + chevron right),
 *      tappable to detail. The embryo card used a flat circle-plus-text
 *      layout.
 *   2. The "See recommended supplements" CTA is replaced with a
 *      Guided Program row carrying a Coming Soon badge. Same row
 *      chrome as everything else.
 *   3. Header is the canonical step pattern (icon + title + subtitle +
 *      chevron, collapsible via the Collapsible primitive).
 */
import { View, Text, Pressable } from "react-native";
import { useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Leaf,
  Pill,
  Sparkles,
  TrendingUp,
  Users,
} from "lucide-react-native";
import type { LucideIcon } from "lucide-react-native";
import { Collapsible } from "@louise/moon-ui";
import { EMBER, GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import type { OptimizationWindow, PlanAction } from "../plan.types";

type Props = {
  optimization: OptimizationWindow;
  userAge?: number;
  showPreviewTag?: boolean;
  onPressCategory?: (key: CategoryKey) => void;
  onPressProgram?: () => void;
  onAction?: (action: PlanAction) => void;
  /** When true, render the partner supplements row. Defaults to true
   *  while we're previewing the design.wired to partnerChoice later. */
  hasPartner?: boolean;
};

type CategoryKey =
  "supplements" | "lifestyle" | "partner-supplements" | "track" | "program";

type CategoryTile = {
  key: CategoryKey;
  icon: LucideIcon;
  title: string;
  subtitle: string;
  badge?: string;
};

const TEXT = "#2A2522";
const TEXT_LIGHT = "#8A7D74";
// Same neutral brown chip used for supplement dosage in SupplementRow.
const BROWN = "#6B5548";
const BROWN_BG = "rgba(139, 111, 94, 0.1)";

function CanonicalRow({
  tile,
  onPress,
}: {
  tile: CategoryTile;
  onPress?: () => void;
}) {
  const Icon = tile.icon;
  const capturePress = usePressCapture("CanonicalRow");
  return (
    <Pressable
      onPress={
        onPress
          ? () => {
              capturePress(pressLabel(tile.title));
              onPress();
            }
          : undefined
      }
      disabled={!onPress}
      style={{
        ...(GLASS_CARD_STYLE as object),
        overflow: "hidden",
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 12,
          paddingHorizontal: 16,
          paddingVertical: 12,
        }}
      >
        <View
          style={{
            width: 28,
            height: 28,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon size={20} color={EMBER.colors.warm} strokeWidth={1.5} />
        </View>

        <View style={{ flex: 1, gap: 2 }}>
          <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
            <Text
              className="font-dm-sans"
              style={{ fontSize: 14, fontWeight: "600", color: TEXT }}
            >
              {tile.title}
            </Text>
            {tile.badge ? (
              <View
                style={{
                  backgroundColor: BROWN_BG,
                  paddingHorizontal: 8,
                  paddingVertical: 2,
                  borderRadius: 6,
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{ fontSize: 11, fontWeight: "700", color: BROWN }}
                >
                  {tile.badge}
                </Text>
              </View>
            ) : null}
          </View>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              color: TEXT_LIGHT,
              lineHeight: 17,
            }}
          >
            {tile.subtitle}
          </Text>
        </View>

        <ChevronRight size={14} color={TEXT_LIGHT} strokeWidth={1.5} />
      </View>
    </Pressable>
  );
}

export default function OptimizationCardV2({
  showPreviewTag = true,
  onPressCategory,
  onPressProgram,
  hasPartner = true,
}: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("OptimizationCardV2");
  const [expanded, setExpanded] = useState(true);

  const tiles: CategoryTile[] = [
    {
      key: "supplements",
      icon: Pill,
      title: t("homepage.optimization_card_v2.start_supplements"),
      subtitle: t("homepage.optimization_card_v2.supplements_subtitle"),
    },
    {
      key: "lifestyle",
      icon: Leaf,
      title: t("homepage.shared.lifestyle_factors"),
      subtitle: t("homepage.optimization_card_v2.lifestyle_subtitle"),
    },
    ...(hasPartner
      ? [
          {
            key: "partner-supplements" as CategoryKey,
            icon: Users,
            title: t("homepage.optimization_card_v2.partner_start_supplements"),
            subtitle: t(
              "homepage.optimization_card_v2.partner_supplements_subtitle",
            ),
            badge: t("homepage.shared.partner"),
          },
        ]
      : []),
    {
      key: "track",
      icon: TrendingUp,
      title: t("homepage.optimization_card_v2.track_progress"),
      subtitle: t("homepage.optimization_card_v2.track_progress_subtitle"),
    },
    {
      key: "program",
      icon: Sparkles,
      title: t("homepage.optimization_card_v2.guided_program"),
      subtitle: t("homepage.optimization_card_v2.guided_program_subtitle"),
      badge: t("homepage.optimization_card_v2.coming_soon"),
    },
  ];

  return (
    <View
      style={{
        ...(GLASS_CARD_STYLE as object),
        marginTop: 16,
        overflow: "hidden",
      }}
    >
      {/* Step header: icon + title + subtitle + chevron, mirrors the
          embryo-freezing "Optimize Before Your Cycle" stepper step */}
      <Pressable
        onPress={() => {
          capturePress(
            pressLabel(
              t("homepage.optimization_card_v2.optimize_before_cycle"),
            ),
            "homepage.optimization_card_v2.optimize_before_cycle",
          );
          setExpanded(!expanded);
        }}
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 12,
          paddingHorizontal: 16,
          paddingTop: 16,
          paddingBottom: expanded ? 12 : 16,
        }}
      >
        <View
          style={{
            width: 28,
            height: 28,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Sparkles size={20} color={EMBER.colors.warm} strokeWidth={1.5} />
        </View>

        <View style={{ flex: 1, gap: 2 }}>
          <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
            <Text
              className="font-dm-sans"
              style={{ fontSize: 15, fontWeight: "600", color: TEXT }}
            >
              {t("homepage.optimization_card_v2.optimize_before_cycle")}
            </Text>
            {showPreviewTag ? (
              <View
                style={{
                  backgroundColor: BROWN_BG,
                  paddingHorizontal: 8,
                  paddingVertical: 2,
                  borderRadius: 6,
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{ fontSize: 11, fontWeight: "700", color: BROWN }}
                >
                  V2
                </Text>
              </View>
            ) : null}
          </View>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              color: TEXT_LIGHT,
              lineHeight: 17,
            }}
          >
            {t("homepage.optimization_card_v2.things_to_start_now")}
          </Text>
        </View>

        {expanded ? (
          <ChevronUp size={18} color={TEXT_LIGHT} strokeWidth={1.5} />
        ) : (
          <ChevronDown size={18} color={TEXT_LIGHT} strokeWidth={1.5} />
        )}
      </Pressable>

      <Collapsible expanded={expanded}>
        <View style={{ paddingHorizontal: 16, paddingBottom: 16, gap: 14 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              color: EMBER.colors.textLight,
              lineHeight: 19,
            }}
          >
            {t("homepage.optimization_card_v2.egg_quality_paragraph")}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              color: EMBER.colors.textLight,
              lineHeight: 19,
            }}
          >
            {t("homepage.optimization_card_v2.evidence_paragraph")}
          </Text>

          <View style={{ gap: 10 }}>
            {tiles.map((tile) => (
              <CanonicalRow
                key={tile.key}
                tile={tile}
                onPress={
                  tile.key === "program"
                    ? onPressProgram
                    : onPressCategory
                      ? () => onPressCategory(tile.key)
                      : undefined
                }
              />
            ))}
          </View>
        </View>
      </Collapsible>
    </View>
  );
}
